import { useGetStatisticOperatorInfoQuery } from '@app/store/api/qm-module/qm-statistic/qm-statistic-page.api';
import React, { useEffect, useMemo, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { FormTabSwitcher, Select } from '@ui';

import { StatisticWidgetsRangeType } from '@app/interfaces/qm-module/qm-statistic/qm-statistic.type';

import useTranslation from '@app/hooks/use-translation';

import Table from '@app/components/table';

import { FilterTableData, TableTitle } from '@app/components/table/table.type';

import { StatisticColorByStepCalc } from '@app/utils/statistic-color-by-step';

import { useGetUserInfoQuery } from '@app/store/api/get-user-info.api';

import { StatisticOutletContextType } from '../../type';
import RangeBlock from '../range-block';
import { useGetStatisticLinkObject } from '../../hooks/useGetStatisticLinkObject';

const OperatorsTapContent = () => {
  const { t } = useTranslation('pages.qmManage.statistic');

  const params = useParams();
  const { team_id, mform_id } = params;
  const { data } = useGetUserInfoQuery();
  const role = data?.qm_role;
  const { teamsList, setNavigationItems } = useOutletContext<StatisticOutletContextType>();
  const [localTeam, setLocalTeam] = useState(team_id);
  const [tableSort, changeTableSort] = useState<FilterTableData | undefined>({
    sortBy: 'operator',
    sortDesc: true,
  });

  const { widgetsFilterBlock, updateUrl } = useGetStatisticLinkObject(setNavigationItems);

  // TODO:
  const [localRange, setLocalRange] = useState(widgetsFilterBlock?.range);

  const { data: operatorsStatisticData, isLoading } = useGetStatisticOperatorInfoQuery(
    {
      params: {
        mform_id: mform_id || '',
        ...{ ...(role !== 'operator' ? { team_id: localTeam } : {}) },
      },
      body: { range: localRange, ...tableSort },
    },
    { skip: !mform_id },
  );

  useEffect(() => {
    setLocalRange(widgetsFilterBlock?.range);
  }, [widgetsFilterBlock?.range]);

  function handlerDateDropTrigger(key: StatisticWidgetsRangeType['type']) {
    if (key === 'r') {
      // changeShowingModelDatePicker(true);
      return;
    }

    setLocalRange((prev) => {
      const changedObj = { dt_type: prev?.dt_type, type: key, parameters: [] as [] };
      updateUrl({ ...widgetsFilterBlock, range: changedObj });
      return changedObj;
    });
  }

  function handleChangeDtType(value: string) {
    setLocalRange((prev) => {
      const changedObj = {
        dt_type: value as 'start' | 'processing',
        type: prev?.type as any,
        parameters: prev?.parameters,
      };
      updateUrl({ ...widgetsFilterBlock, range: changedObj });
      return changedObj;
    });
  }

  const dtTypeValuesList = [
    { value: 'start', title: t('dt_type.start') },
    { value: 'processing', title: t('dt_type.processing') },
  ];

  const dynamicCols = useMemo(
    () =>
      [...(operatorsStatisticData?.table?.[0]?.criteria || [])]
        .sort((a, b) => {
          const nameA = a.name.toUpperCase(); // Удобно для регистронезависимой сортировки
          const nameB = b.name.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        })
        ?.map<TableTitle>((criteria) => ({
          index: criteria.id,
          title: criteria.name,
          filter: true,
        })) || [],
    [operatorsStatisticData?.table],
  );

  const tableColumns: TableTitle[] = [
    {
      index: 'operator',
      title: t('name'),
      filter: true,
      size: 140,
      rowClassName: '!justify-start pl-1',
    },
    ...dynamicCols,
    { index: 'score_avg', title: t('score_avg'), size: 140, filter: true },
  ];

  const standardColor = StatisticColorByStepCalc;

  const tableData = operatorsStatisticData?.table.map((table) => {
    const criteriaCols = table?.criteria?.reduce((acc, crit) => {
      acc = {
        ...acc,
        [crit.id]: (
          <div
            className="h-full w-full text-center"
            style={{
              background: standardColor({ max: crit.score_max_calc, score: crit.rating_avg }),
            }}
          >
            {crit.rating_avg}
          </div>
        ),
      };
      return acc;
    }, {});

    return {
      operator: table.operator.name,
      score_avg: (
        <div
          className="h-full w-full text-center"
          style={{
            background: table.score_avg.conditional_formatting
              ? table.score_avg.conditional_formatting.color
              : standardColor({
                  max: table.score_avg.score_max_calc,
                  score: table.score_avg.value,
                }),
          }}
        >
          {table.score_avg.value}
        </div>
      ),
      ...criteriaCols,
    };
  });

  function onChangeFilter(data: FilterTableData | undefined) {
    changeTableSort(data);
  }

  return (
    <div>
      <div className="flex items-baseline gap-[20px] ">
        {!!teamsList?.qm_teams.length && (
          <Select
            withSearch
            options={
              teamsList?.qm_teams.map((team) => ({ value: team.team_id, title: team.name })) || []
            }
            defaultValue={localTeam}
            onChange={(value) => setLocalTeam(String(value))}
          />
        )}
        <div className="min-w-[500px] max-w-[500px]">
          <FormTabSwitcher
            classname="!bg-[#efefef]"
            valuesList={dtTypeValuesList}
            isActive={localRange?.dt_type || 'start'}
            onChange={handleChangeDtType}
          />
        </div>
        <div className="flex w-full justify-end">
          <RangeBlock
            activeKey={localRange?.type}
            rangeFromStor={localRange}
            onChange={(key) => handlerDateDropTrigger(key as StatisticWidgetsRangeType['type'])}
          />
        </div>
      </div>
      <Table
        default_width={4.5}
        hideDragMode
        truncate={false}
        onFilter={onChangeFilter}
        isPending={isLoading}
        columns={tableColumns}
        dataSource={tableData}
      />
    </div>
  );
};

export default OperatorsTapContent;
